/**
 * "Editing a client record" walkthrough.
 *
 * Starts on the AML Clients list. The user clicks any row to open the client
 * detail page, then clicks Edit Details to open the edit modal. The tour
 * stops once the modal is open and explains the form — it does not require
 * the user to actually save changes (so they can edit real data at their own
 * pace, or close out without saving).
 *
 * Targets used:
 *   `tr.clickable` (in ClientList)            — any client row
 *   data-tour="edit-client-btn"               — Edit Details button on the overview
 *   data-tour="edit-client-save"              — Save Changes button in edit modal
 *   data-tour="edit-client-cancel"            — Cancel button in edit modal
 *
 * Field list and labels mirror ClientOverview.vue's edit modal as of writing —
 * keep in sync if fields are added or renamed there.
 */

import { NEXT, BACK, START, SKIP, DONE, advanceWhenAppears } from './_helpers.js';

function waitForElement(selector, timeout = 10000) {
    return new Promise((resolve) => {
        if (document.querySelector(selector)) return resolve();
        const start = Date.now();
        const check = () => {
            if (document.querySelector(selector)) return resolve();
            if (Date.now() - start > timeout) return resolve();
            setTimeout(check, 50);
        };
        check();
    });
}

export const editClientTour = {
    id: 'edit-client',
    title: 'Editing a client record',
    description: 'Walks through finding a client, opening their record, and editing their core details.',
    autoLaunchOn: { page: 'clients' },

    steps: [
        {
            id: 'intro',
            title: 'Editing a client record',
            text: `Once a client is onboarded, you can edit their core details (name, type,
                contact info, assigned staff, etc.) at any time. This tour walks through
                where to find that.`,
            buttons: [START],
        },
        {
            id: 'pick-row',
            attachTo: { element: 'table.aml_table tbody tr.clickable', on: 'top' },
            beforeShowPromise: () => waitForElement('table.aml_table tbody tr.clickable'),
            title: 'Open a client',
            text: `Click any row in the client list to open that client's detail page.
                You can also use the search box and filters above to narrow down to the
                client you want.`,
            // Watch for the next step's anchor to appear instead of using
            // `advanceOn: click` — the latter only attaches to the first
            // matching row, so clicking any other row would leave the
            // tour stranded on this step.
            when: advanceWhenAppears('[data-tour="edit-client-btn"]'),
        },
        {
            id: 'edit-btn',
            attachTo: { element: '[data-tour="edit-client-btn"]', on: 'left' },
            beforeShowPromise: () => waitForElement('[data-tour="edit-client-btn"]'),
            title: 'Click "Edit Details"',
            text: `On the client's overview panel, the <strong>Edit Details</strong> button
                opens an edit modal with all the editable fields.`,
            advanceOn: { selector: '[data-tour="edit-client-btn"]', event: 'click' },
        },
        {
            id: 'edit-form',
            attachTo: { element: '[data-tour="edit-client-save"]', on: 'top' },
            beforeShowPromise: () => waitForElement('[data-tour="edit-client-save"]'),
            title: 'The edit form',
            text: `You can update:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Client / Entity Name</strong>, <strong>Former Names</strong>,
                        <strong>Client Type</strong>, <strong>Date of Birth</strong>,
                        <strong>ABN / ACN</strong></li>
                    <li><strong>Address</strong>, <strong>Email</strong>, <strong>Phone</strong></li>
                    <li><strong>Assigned Staff</strong> — who owns this client internally</li>
                    <li><strong>Pre-commencement</strong> — flag for clients existing before
                        the regime change</li>
                    <li><strong>Trustee Name(s)</strong> — appears for Trust client types</li>
                </ul>
                Risk rating, CDD status, designated services and verification details
                are managed elsewhere on the client page (separate sections), not here.
                <br><br>
                Click <strong>Save Changes</strong> to persist your edits, or
                <strong>Cancel</strong> to discard them.`,
            buttons: [DONE],
        },
    ],
};
